import * as Popover from "@radix-ui/react-popover";
import {
  AlertTriangle,
  BarChart3,
  CheckCircle,
  ClipboardList,
  Clock3,
  Download,
  Edit2,
  FileCheck,
  FileText,
  FolderOpen,
  LayoutGrid,
  List,
  Loader2,
  LogOut,
  MoreVertical,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  User,
  X,
} from "lucide-react";

import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useAudit } from "../../contexts/audit-context";

import { useSite } from "../../contexts/site-context";

import { useAuth } from "../../hooks/use-auth";

import { sharePendingRead } from "../../lib/sharedRead";

import { Badge } from "../ui/badge";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../ui/alert-dialog";

import { Button } from "../ui/button.jsx";

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card.jsx";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog.jsx";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import { Footer } from "../ui/footer.jsx";

import { Input } from "../ui/input.jsx";

import { Progress } from "../ui/progress";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

import { ThemeToggle } from "../ui/theme-toggle";

import { NewAuditModal } from "./new-audit-modal";

import { NotificationsDropdown } from "./notifications-dropdown";

import { ReportModal } from "./report-modal";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";

import {
  fetchSiteData,
  setCurrentSiteData,
} from "../../redux/slices/siteDataSlice.js";

import { fetchSiteOverviewData } from "../../redux/slices/siteOverviewDataSlice.js";

import { useSearch } from "../../contexts/user-search";

import { useToast } from "../../hooks/use-toast";

import {
  createReport,
  fetchAllReportsWithData,
  fetchRecentWorkingReports,
  removeReport,
  setCurrentReport,
  upsertReport,
} from "../../redux/slices/auditReportSlice.js";

import { SessionProgressSearch } from "./session-progress-search";

import auditReportService from "../../services/auditReportService.js";

import {
  templateComponents,
  templateOverallProgress,
} from "../../utils/componentUtils.js";

import ReportTableView from "./report-table-view"; // Adjust path as needed

// Predefined templates
const PREDEFINED_TEMPLATES = [
  {
    name: "Pre-Construction Audit",
    description:
      "Planning and feasibility assessment before construction begins",
    id: "pre-construction",
    icon: "📋",
    badgeColor: "bg-blue-100 text-blue-800",
    sections: 2,
  },
  {
    name: "Under-Construction Audit",
    description: "Monthly monitoring during active construction phase",
    id: "under-construction",
    icon: "🏗️",
    badgeColor: "bg-yellow-100 text-yellow-800",
    sections: 3,
  },
  {
    name: "Operational Project Audit",
    description: "Post-construction assessment and operational review",
    id: "operational",
    icon: "⚡",
    badgeColor: "bg-green-100 text-green-800",
    sections: 2,
  },
  {
    name: "Punch List & Final Inspection",
    description: "Final defect list and project completion review",
    id: "punch-list",
    icon: "✅",
    badgeColor: "bg-purple-100 text-purple-800",
    sections: 1,
  },
];

// Keep all report filters together in a keyboard-accessible popover.
const FilterBar = ({
  search,
  onSearchChange,
  templateFilter,
  onTemplateFilterChange,
  statusFilter = "all",
  onStatusFilterChange = undefined,
  sort,
  onSortChange,
  templates,
  showStatusFilter = false,
  onClearAll,
  viewMode,
  setViewMode,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const activeCount =
    Number(templateFilter !== "all") +
    Number(showStatusFilter && statusFilter !== "all") +
    Number(sort !== "updated-desc");

  useEffect(() => {
    const savedViewMode = localStorage.getItem("reportViewMode");
    if (savedViewMode === "grid" || savedViewMode === "table") {
      setViewMode(savedViewMode);
    }
  }, [setViewMode]);

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-0 basis-full sm:basis-0 sm:flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-muted-foreground" />
        <Input
          aria-label="Search reports"
          placeholder="Search reports..."
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          className="pl-9 pr-10 bg-slate-50 dark:bg-surface-raised/50 border-slate-200 dark:border-border"
        />
        {search && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onSearchChange("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 dark:bg-muted">
        {(
          [
            ["grid", "Grid View", LayoutGrid],
            ["table", "Table View", List],
          ] as const
        ).map(([mode, label, Icon]) => (
          <Button
            key={mode}
            size="sm"
            variant="ghost"
            title={label}
            aria-label={label}
            aria-pressed={viewMode === mode}
            onClick={() => setViewMode(mode)}
            className={`h-8 px-2 ${viewMode === mode ? "bg-white shadow-sm dark:bg-accent" : ""}`}
          >
            <Icon className="h-4 w-4" />
          </Button>
        ))}
      </div>
      <Popover.Root open={isExpanded} onOpenChange={setIsExpanded}>
        <Popover.Trigger asChild>
          <Button
            variant="outline"
            className={`gap-2 rounded-lg ${activeCount ? "border-blue-300 bg-blue-50 text-blue-700 dark:border-primary-border dark:bg-brand/20 dark:text-link" : ""}`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border px-1 text-xs text-white">
                {activeCount}
              </span>
            )}
          </Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="end"
            sideOffset={8}
            collisionPadding={16}
            aria-label="Report filters"
            className="z-50 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-border dark:bg-card"
          >
            <div className="flex items-start justify-between border-b border-slate-100 bg-slate-50/80 px-5 py-4 dark:border-border dark:bg-surface/40">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-foreground">
                  Filter reports
                </h3>
                <p className="mt-1 text-xs text-slate-500 dark:text-muted-foreground">
                  Choose a template and how reports are sorted.
                </p>
              </div>
              <Popover.Close asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Close filters"
                  className="h-7 w-7 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </Popover.Close>
            </div>
            <div className="space-y-5 p-5">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-muted-foreground">
                  Report template
                </label>
                <Select
                  value={templateFilter}
                  onValueChange={onTemplateFilterChange}
                >
                  <SelectTrigger
                    className="w-full"
                    aria-label="Report template"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Templates</SelectItem>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {showStatusFilter && (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-muted-foreground">
                    Status
                  </label>
                  <Select
                    value={statusFilter}
                    onValueChange={onStatusFilterChange}
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-label="Report status"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="in-progress">In Progress</SelectItem>
                      <SelectItem value="generated">Generated</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-muted-foreground">
                  Sort by
                </label>
                <Select value={sort} onValueChange={onSortChange}>
                  <SelectTrigger className="w-full" aria-label="Sort reports">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated-desc">Latest Updated</SelectItem>
                    <SelectItem value="updated-asc">Oldest Updated</SelectItem>
                    <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                    <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                    <SelectItem value="progress-desc">
                      Highest Progress
                    </SelectItem>
                    <SelectItem value="progress-asc">
                      Lowest Progress
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 px-5 py-3 dark:border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearAll}
                disabled={!activeCount && !search}
              >
                Reset all
              </Button>
              <Popover.Close asChild>
                <Button
                  size="sm"
                  className="bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border text-white hover:bg-blue-700"
                >
                  Done
                </Button>
              </Popover.Close>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};

const getStatusColor = (status) => {
  switch (status) {
    case "completed":
      return "text-green-600";
    case "in-progress":
      return "text-blue-600";
    case "pending":
      return "text-slate-400";
    default:
      return "text-slate-400";
  }
};

export function DashboardContent({ sessionProgress = {} }) {
  // Audit sections and the dashboard share the document's scroll position.
  // Reset only on entry, before paint, so dashboard updates keep their position.
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const { user, logout } = useAuth();
  const userId = user?._id || user?.id;
  const { setSelectedSite: setAuditSelectedSite, overallProgress } = useAudit();

  const { sites = [], currentSite = null } = useSite();

  const navigate = useNavigate();
  const [showNewAuditModal, setShowNewAuditModal] = useState(false);

  const [showReportModal, setShowReportModal] = useState(false);
  const [showCreateReportDialog, setShowCreateReportDialog] = useState(false);
  const [newReportName, setNewReportName] = useState("");
  const [newReportTemplateId, setNewReportTemplateId] = useState("");
  const [creatingReport, setCreatingReport] = useState(false);
  const [createReportSiteId, setCreateReportSiteId] = useState("");
  const recentReports = useAppSelector(
    (state) => state.auditReports.recentReports,
  );
  const allReportsData = useAppSelector(
    (state) => state?.auditReports?.allReportsData || {},
  );
  const isOnline = useAppSelector((state) => state?.offline?.isOnline ?? true);
  const [loadingReports, setLoadingReports] = useState(false);
  const [recentReportSearch, setRecentReportSearch] = useState("");
  const [recentReportTemplateFilter, setRecentReportTemplateFilter] =
    useState("all");
  const [recentReportSort, setRecentReportSort] = useState("updated-desc");

  const [reportData, setReportData] = useState(null);
  const [editingReport, setEditingReport] = useState<any>(null);
  const [deletingReport, setDeletingReport] = useState<any>(null);
  const [editReportName, setEditReportName] = useState("");
  const [updatingReportName, setUpdatingReportName] = useState(false);
  const [reportActionLoading, setReportActionLoading] = useState<{
    reportId: string;
    action: "save" | "generate" | "regenerate" | "download" | "edit" | "delete";
  } | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState(null);

  const [auditSectionsData, setAuditSectionsData] = useState(
    templateComponents[selectedTemplateName] || [],
  );
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  useEffect(() => {
    if (!showCreateReportDialog) return;

    if (currentSite?._id || currentSite?.id) {
      const nextSiteId = currentSite._id || currentSite.id;
      setCreateReportSiteId((prev) => prev || nextSiteId);
    } else {
      setCreateReportSiteId("");
    }
  }, [showCreateReportDialog, currentSite?._id, currentSite?.id]);

  useEffect(() => {
    setAuditSectionsData(templateComponents[selectedTemplateName] || []);
  }, [selectedTemplateName]);

  const { clearSearch } = useSearch(auditSectionsData, sites, currentSite);

  const handleSectionSelect = (sectionId) => {
    if (currentSite) {
      const activeReportId =
        window.sessionStorage?.getItem("activeReportId") ||
        window.localStorage?.getItem("activeReportId");
      if (activeReportId) {
        navigate(`/audit/${currentSite?.id}/${activeReportId}/${sectionId}`);
      }
    }
  };

  const getFirstSectionForTemplate = (templateId) =>
    templateComponents[templateId]?.[0]?.name || "site-overview";

  const openReport = (report) => {
    if (report) {
      setReportData(report);
      dispatch(setCurrentReport(report));
    }
    if (!report?._id) return;
    const reportSiteId = report.siteId?._id || report.siteId;
    const matchingSite = sites.find(
      (site) => String(site.id || site._id) === String(reportSiteId),
    );
    if (matchingSite) {
      // Fetch site overview data for the opened report
      dispatch(
        fetchSiteOverviewData({ siteId: reportSiteId, reportId: report._id }),
      );
      setAuditSelectedSite(matchingSite);
    }
    window.sessionStorage?.setItem("activeReportId", report._id);
    window.localStorage?.setItem("activeReportId", report._id);
    setSelectedTemplateName(report.templateId);

    navigate(
      `/audit/${reportSiteId || currentSite?._id || currentSite?.id}/${report._id}/${getFirstSectionForTemplate(report.templateId)}`,
    );
  };

  const refreshReports = useCallback(
    async ({ force = false } = {}) => {
      if (!userId) return;
      setLoadingReports(true);
      try {
        await sharePendingRead(
          dispatch,
          ["dashboard", userId],
          async () => {
            const results = await Promise.allSettled([
              dispatch(fetchRecentWorkingReports()).unwrap(),
              dispatch(fetchAllReportsWithData()).unwrap(),
              dispatch(fetchSiteData({ userId })).unwrap(),
            ]);
            const failure = results.find(
              (result) => result.status === "rejected",
            );
            if (failure?.status === "rejected") throw failure.reason;
          },
          { force },
        );
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        setLoadingReports(false);
      }
    },
    [dispatch, userId],
  );

  const handleCreateReport = async () => {
    const reportSiteId =
      createReportSiteId || currentSite?._id || currentSite?.id;
    if (!reportSiteId) {
      toast.warning("Site required", "Choose a site before creating a report.");
      return;
    }
    if (!newReportTemplateId || !newReportName.trim()) return;

    const template = PREDEFINED_TEMPLATES.find(
      (t) => t.id === newReportTemplateId,
    );
    try {
      setCreatingReport(true);
      const result = await dispatch(
        createReport({
          siteId: reportSiteId,
          templateId: newReportTemplateId,
          templateName: template?.name || newReportTemplateId,
          reportName: newReportName.trim(),
        }),
      ).unwrap();
      const report = result?.report || result;
      setShowCreateReportDialog(false);
      setNewReportName("");
      setNewReportTemplateId("");
      setCreateReportSiteId("");
      await refreshReports({ force: true });
      openReport(report);
    } catch (error) {
      console.error("Failed to create report:", error);
    } finally {
      setCreatingReport(false);
    }
  };

  const handleCreateReportDialogChange = (open) => {
    setShowCreateReportDialog(open);
    if (!open) {
      setCreateReportSiteId("");
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "/" && !event.ctrlKey && !event.metaKey) {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
      }
      if (event.key === "Escape") {
        clearSearch();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [clearSearch]);

  const siteProgress = useMemo(() => {
    const values = Object.values(
      sessionProgress?.[selectedTemplateName] || {},
    ).map(Number);
    const total = values.reduce((sum, val) => sum + val, 0);
    if (values.length === 0) return 0;
    return Math.round(total / values.length);
  }, [sessionProgress, selectedTemplateName]);

  useEffect(() => {
    refreshReports();
  }, [refreshReports]);

  const templateLabel = (templateId) =>
    PREDEFINED_TEMPLATES.find((template) => template.id === templateId)?.name ||
    templateId ||
    "Untitled template";

  const formatReportDate = (date) => {
    if (!date) return "No activity yet";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const reportMatchesFilters = (
    report,
    search,
    templateFilter,
    statusFilter = "all",
  ) => {
    const searchText = search.trim().toLowerCase();
    const haystack = [
      report.reportName,
      report.siteName,
      report.templateName,
      report.templateId,
      report.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      (!searchText || haystack.includes(searchText)) &&
      (templateFilter === "all" || report.templateId === templateFilter) &&
      (statusFilter === "all" || report.status === statusFilter)
    );
  };

  const getReportProgress = useCallback(
    (report) => {
      // Prefer locally persisted full report data while offline. If the full
      // cache has not hydrated yet, retain the optimistic progress stored on
      // the report card instead of falling back to zero/server-only state.
      const reportData = allReportsData?.[report._id] || null;
      const progressSource = reportData || report;
      const calculatedProgress = templateOverallProgress(progressSource);
      const progress =
        !isOnline &&
        calculatedProgress === 0 &&
        Number.isFinite(Number(report?.completionPercentage))
          ? Number(report.completionPercentage)
          : calculatedProgress;
      return progress;
    },
    [allReportsData, isOnline],
  );

  const sortReports = (reports, sort) => {
    const sorted = [...reports];
    sorted.sort((a, b) => {
      if (sort === "name-asc") {
        return (a.reportName || "").localeCompare(b.reportName || "");
      }
      if (sort === "name-desc") {
        return (b.reportName || "").localeCompare(a.reportName || "");
      }
      if (sort === "progress-desc") {
        return getReportProgress(b) - getReportProgress(a);
      }
      if (sort === "progress-asc") {
        return getReportProgress(a) - getReportProgress(b);
      }
      const aDate = new Date(
        a.updatedAt || a.savedAt || a.createdAt || 0,
      ).getTime();
      const bDate = new Date(
        b.updatedAt || b.savedAt || b.createdAt || 0,
      ).getTime();
      if (sort === "updated-asc") {
        return aDate - bDate;
      }
      return bDate - aDate;
    });
    return sorted;
  };

  const filteredRecentReports = useMemo(
    () =>
      sortReports(
        recentReports.filter((report) =>
          reportMatchesFilters(
            report,
            recentReportSearch,
            recentReportTemplateFilter,
          ),
        ),
        recentReportSort,
      ),
    [
      recentReports,
      recentReportSearch,
      recentReportTemplateFilter,
      recentReportSort,
      getReportProgress,
    ],
  );

  const updateReportInLists = useCallback(
    (reportId, updatedReport) => {
      const normalized = updatedReport || {};
      dispatch(upsertReport({ ...normalized, _id: reportId }));
      if (reportData?._id === reportId) {
        setReportData((prev) =>
          prev?._id === reportId ? { ...prev, ...normalized } : prev,
        );
        dispatch(
          setCurrentReport({ ...reportData, ...normalized, _id: reportId }),
        );
      }
    },
    [dispatch, reportData],
  );

  const handleGenerateReport = async (
    report,
    action: "generate" | "regenerate" = "generate",
  ) => {
    if (!report?._id) return;
    if (!isOnline) {
      toast.warning(
        "You're offline",
        "Connect to the internet to generate this report.",
      );
      return;
    }
    const hasBeenSaved = Boolean(
      report.savedAt ||
      report.status === "in-progress" ||
      report.status === "completed" ||
      report.status === "generated" ||
      report.status === "reviewed" ||
      report.status === "archived",
    );
    if (!hasBeenSaved) {
      toast.warning(
        "Generate unavailable",
        "Save the report first before generating it.",
      );
      return;
    }

    setReportActionLoading({
      reportId: report._id,
      action,
    });
    try {
      const res = await auditReportService.generateCurrentReport(report._id);
      const updatedReport =
        res?.data?.data?.auditReport ||
        res?.data?.auditReport ||
        res?.data?.data ||
        report;
      updateReportInLists(report._id, updatedReport);
      toast.success(
        "Report generated",
        "The report generation request completed.",
      );
    } catch (error) {
      console.error("Failed to generate report:", error);
      toast.error(
        "Generation failed",
        "The report could not be generated right now.",
      );
    } finally {
      setReportActionLoading(null);
    }
  };

  const handleDownloadReport = async (report) => {
    if (!report?._id) return;
    if (!isOnline) {
      toast.warning(
        "You're offline",
        "Connect to the internet to download this report.",
      );
      return;
    }
    const hasBeenGenerated = Boolean(
      report.generatedReport?.generated ||
      report.generatedReport?.fileUrl ||
      ["generated", "completed", "reviewed", "archived"].includes(
        report.status,
      ),
    );
    if (!hasBeenGenerated) {
      toast.warning(
        "Download unavailable",
        "Generate the report before downloading it.",
      );
      return;
    }

    setReportActionLoading({
      reportId: report._id,
      action: "download",
    });
    try {
      const res = await auditReportService.downloadCurrentReport(report._id);

      const fileUrl = res?.data?.data?.fileUrl;
      const fileName = res?.data?.data?.fileName;

      if (!fileUrl) {
        throw new Error("File URL not found");
      }

      // window.open(fileUrl, "_blank", "noopener,noreferrer");
      const link = document.createElement("a");

      link.href = fileUrl;
      link.target = "_blank";
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(
        "Report opened",
        "The report has been opened in a new tab.",
      );
    } catch (error) {
      console.error("Failed to open report:", error);
      toast.error(
        "Open failed",
        "The report could not be opened at the moment.",
      );
    } finally {
      setReportActionLoading(null);
    }
  };

  const handleEditReport = (report) => {
    if (!report?._id) return;

    setEditingReport(report);
    setEditReportName(report.reportName || "");
  };

  const handleSaveReportName = async () => {
    if (!editingReport?._id) return;
    const trimmedName = editReportName.trim();
    if (!trimmedName) {
      toast.warning(
        "Name required",
        "Enter a report name before saving the change.",
      );
      return;
    }

    setUpdatingReportName(true);
    setReportActionLoading({
      reportId: editingReport._id,
      action: "edit",
    });
    try {
      const res = await auditReportService.updateReport(editingReport._id, {
        reportName: trimmedName,
      });
      const updatedReport =
        res?.data?.data?.auditReport ||
        res?.data?.auditReport ||
        res?.data?.data ||
        editingReport;
      updateReportInLists(editingReport._id, updatedReport);
      toast.success("Report renamed", "The report name was updated.");
      setEditingReport(null);
      setEditReportName("");
    } catch (error) {
      console.error("Failed to rename report:", error);
      toast.error("Rename failed", "The report name could not be changed.");
    } finally {
      setUpdatingReportName(false);
      setReportActionLoading(null);
    }
  };

  const handleDeleteReport = async (reportToDelete) => {
    if (!isOnline) {
      toast.warning(
        "You're offline",
        "Connect to the internet to delete this report.",
      );
      return;
    }
    if (reportActionLoading) return;
    setDeletingReport(reportToDelete);
  };

  const handleConfirmDeleteReport = async () => {
    if (!deletingReport || reportActionLoading || !isOnline) return;
    const reportToDelete = deletingReport;
    setReportActionLoading({
      reportId: reportToDelete._id,
      action: "delete",
    });
    try {
      await auditReportService.deleteReport(reportToDelete._id);
      dispatch(removeReport(reportToDelete._id));
      setDeletingReport(null);

      if (reportData?._id === reportToDelete._id) {
        setReportData(null);
      }

      toast.success(
        "Report deleted",
        "The report was removed from the dashboard.",
      );
    } catch (error) {
      console.error("Failed to delete report:", error);
      toast.error(
        "Delete failed",
        "The report could not be deleted right now.",
      );
    } finally {
      setReportActionLoading(null);
    }
  };

  const renderReportCard = (report, { compact = false } = {}) => {
    const isPastReport = ["generated", "completed"].includes(report.status);
    const progress = getReportProgress(report);
    const reportStatus =
      report.status === "not-started" && progress > 0 && progress < 100
        ? "in-progress"
        : report.status === "not-started" && progress === 100
          ? "completed"
          : report.status;
    const hasGeneratedReport = progress === 100;
    const hasDownloadReport = report?.status === "generated";
    const statusColors = {
      "not-started":
        "border-slate-200 bg-slate-50 text-slate-700 dark:border-border dark:bg-card dark:text-muted-foreground",
      saved:
        "border-blue-200 bg-blue-50 text-blue-700 dark:border-primary-border dark:bg-brand/20 dark:text-link",
      "in-progress":
        "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300",
      generated:
        "border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-300",
      completed:
        "border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-900/20 dark:text-orange-300",
    };

    const isGenerating =
      reportActionLoading?.reportId === report._id &&
      reportActionLoading?.action === "generate";

    const isDownloading =
      reportActionLoading?.reportId === report._id &&
      reportActionLoading?.action === "download";

    const isEditing =
      reportActionLoading?.reportId === report._id &&
      reportActionLoading?.action === "edit";
    const isDeleting =
      reportActionLoading?.reportId === report._id &&
      reportActionLoading?.action === "delete";

    return (
      <Card
        key={report._id}
        className="p-0 group overflow-hidden border-slate-200 bg-white shadow-sm transition-colors duration-200 hover:border-blue-200 hover:shadow-md dark:border-white/10 dark:bg-surface-raised/50 dark:shadow-none dark:hover:border-white/20 dark:hover:bg-surface-raised/75"
        title={`Report: ${report.reportName || "Untitled report"}`}
      >
        <CardContent className={compact ? "p-3" : "p-4"}>
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                      isPastReport
                        ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300"
                        : "bg-blue-50 text-blue-600 dark:bg-brand/30 dark:text-link"
                    }`}
                  >
                    {isPastReport ? (
                      <FileCheck className="h-4 w-4" />
                    ) : (
                      <FileText className="h-4 w-4" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate text-sm font-semibold text-slate-950 dark:text-slate-50">
                      {report.reportName || "Untitled report"}
                    </h3>
                    <p className="truncate text-xs text-slate-500 dark:text-muted-foreground">
                      {report.siteName ||
                        currentSite?.siteName ||
                        "Selected site"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  className={`shrink-0 text-xs font-medium ${statusColors[reportStatus] || statusColors["not-started"]}`}
                >
                  {reportStatus || "not-started"}
                </Badge>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-accent"
                      aria-label={`Actions for ${report.reportName || "Untitled report"}`}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {/* <DropdownMenuItem onClick={() => openReport(report)}>
                      <Eye className="mr-2 h-4 w-4" />
                      Open Report
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        handleSaveReport(report);
                      }}
                      disabled={!canSaveReport || !!reportActionLoading}
                      className={
                        canSaveReport
                          ? "text-blue-700 dark:text-link"
                          : "text-slate-400 opacity-60"
                      }
                    >
                      {isSaving ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}

                      {isSaving ? "Saving..." : "Save Report"}
                    </DropdownMenuItem> */}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        handleGenerateReport(report, "generate");
                      }}
                      disabled={
                        !hasGeneratedReport ||
                        !!reportActionLoading ||
                        !isOnline
                      }
                      title={
                        !isOnline
                          ? "Connect to the internet to generate this report"
                          : undefined
                      }
                      className={
                        hasGeneratedReport && isOnline
                          ? "text-indigo-700 dark:text-indigo-300"
                          : "text-slate-400 opacity-60"
                      }
                    >
                      {isGenerating ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <FileText className="mr-2 h-4 w-4" />
                      )}

                      {isGenerating
                        ? "Generating..."
                        : !isOnline
                          ? "Generate Report (offline)"
                          : "Generate Report"}
                    </DropdownMenuItem>

                    {/* <DropdownMenuItem
                      onClick={(e) =>{
                        e.preventDefault();
                        handleGenerateReport(report, "regenerate");
                      } }
                      disabled={!hasDownloadReport || !!reportActionLoading}
                      className={
                        hasDownloadReport
                          ? "text-indigo-700 dark:text-indigo-300"
                          : "text-slate-400 opacity-60"
                      }
                    >
                      {isRegenerating ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <FileText className="mr-2 h-4 w-4" />
                      )}

                      {isRegenerating ? "Re-Generating..." : "Re-Generate Report"}
                    </DropdownMenuItem> */}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        handleDownloadReport(report);
                      }}
                      disabled={
                        !hasDownloadReport || !!reportActionLoading || !isOnline
                      }
                      title={
                        !isOnline
                          ? "Connect to the internet to download this report"
                          : undefined
                      }
                      className={
                        hasDownloadReport && isOnline
                          ? "text-emerald-700 dark:text-emerald-300"
                          : "text-slate-400 opacity-60"
                      }
                    >
                      {isDownloading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}

                      {isDownloading
                        ? "Opening..."
                        : !isOnline
                          ? "Download Report (offline)"
                          : "Download Report"}
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onClick={(e) => {
                        e.preventDefault();
                        handleEditReport(report);
                      }}
                      disabled={isEditing || !!reportActionLoading}
                      className={
                        isEditing || !!reportActionLoading
                          ? "text-slate-400 opacity-60"
                          : "text-slate-700 dark:text-foreground"
                      }
                    >
                      {isEditing ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Edit2 className="mr-2 h-4 w-4" />
                      )}
                      {isEditing ? "Updating..." : "Edit"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => handleDeleteReport(report)}
                      className={
                        isOnline
                          ? "text-red-600 dark:text-red-400"
                          : "text-slate-400 opacity-60"
                      }
                      disabled={
                        isDeleting || !!reportActionLoading || !isOnline
                      }
                      title={
                        !isOnline
                          ? "Connect to the internet to delete this report"
                          : undefined
                      }
                    >
                      {isDeleting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                      )}

                      {isDeleting ? "Deleting" : "Delete"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-muted-foreground">
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 dark:bg-muted">
                <FolderOpen className="h-3.5 w-3.5" />
                {templateLabel(report.templateId)}
              </span>
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 dark:bg-muted">
                <Clock3 className="h-3.5 w-3.5" />
                {formatReportDate(
                  report.updatedAt || report.savedAt || report.createdAt,
                )}
              </span>
              <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 dark:bg-muted">
                <BarChart3 className="h-3.5 w-3.5" />
                {progress}%
              </span>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 dark:text-muted-foreground">
                  Progress
                </span>
                <span className="font-medium text-slate-900 dark:text-foreground">
                  {progress}%
                </span>
              </div>
              <Progress
                value={progress}
                className={`h-2 transition-all ${
                  progress === 100
                    ? "bg-emerald-100 dark:bg-emerald-900/20"
                    : progress > 50
                      ? "bg-blue-100 dark:bg-brand/20"
                      : "bg-slate-100 dark:bg-muted"
                }`}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                size="sm"
                // disabled={
                //   report.status === "saved" ||
                //   report.status === "generated" ||
                //   progress === 100
                // }
                onClick={() => openReport(report)}
                className="bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border hover:bg-blue-700 text-white shadow-sm hover:shadow transition-all"
              >
                {progress > 0 && progress < 100
                  ? "Continue Audit"
                  : progress === 100
                    ? "Open Audit"
                    : report.status === "not-started"
                      ? "Start Audit"
                      : "Open Audit"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-background flex transition-colors">
      <div className="flex-1 flex flex-col min-w-0 min-h-screen">
        <header className="bg-white dark:bg-surface border-b border-slate-200 dark:border-border shadow-sm sticky top-0 z-30">
          <div className="px-4 py-3 md:px-6 md:py-4">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2 md:gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 md:gap-3">
                    <h1 className="text-base md:text-xl lg:text-xl font-semibold text-slate-900 dark:text-foreground truncate">
                      {currentSite ? currentSite.siteName : "Dashboard"}
                    </h1>
                    {currentSite && (
                      <div className="flex items-center gap-1.5 md:gap-2">
                        <Badge
                          variant="outline"
                          className="text-xs md:text-xs border-slate-300 dark:border-border text-slate-600 dark:text-muted-foreground"
                        >
                          {currentSite?.siteType}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 md:gap-2">
                    <p className="hidden sm:block text-sm md:text-sm text-slate-500 dark:text-muted-foreground truncate mt-0.5">
                      {currentSite
                        ? `${currentSite.location}
                       
                         `
                        : `Welcome back, ${user?.name || "User"}`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1 md:gap-2">
                <div className="flex items-center gap-0.5 md:gap-1">
                  <NotificationsDropdown />
                  <ThemeToggle />
                  <Button
                    title="Start New Site"
                    variant="outline"
                    size="sm"
                    className="h-8 px-2 hover:bg-slate-100 dark:hover:bg-accent transition-colors md:h-8 md:px-2.5"
                    onClick={() => setShowNewAuditModal(true)}
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">New Site</span>
                  </Button>
                </div>

                <div className="flex items-center gap-1.5 md:gap-2 ml-1.5 md:ml-2 pl-1.5 md:pl-2 border-l border-slate-200 dark:border-border">
                  <div className="hidden md:flex items-center gap-1.5 md:gap-2">
                    <div className="text-right">
                      <div className="text-sm font-medium text-slate-900 dark:text-foreground truncate max-w-20 md:max-w-28">
                        {user?.name || "User"}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-muted-foreground truncate max-w-15 md:max-w-20">
                        {user?.email || ""}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-0.5 md:gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="hover:bg-slate-100 dark:hover:bg-accent transition-colors p-2 md:p-2.5"
                      onClick={() => navigate("/profile")}
                      title="Profile Settings"
                    >
                      <User className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors p-2 md:p-2.5"
                      onClick={logout}
                      title="Logout"
                    >
                      <LogOut className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="min-h-screen min-w-0 flex-1 space-y-6 overflow-auto px-4 py-5 sm:px-6 sm:py-6">
          <div className="space-y-6">
            {/* Recent Reports Section with Filters */}
            <section aria-label="Recent reports" className="space-y-5">
              <CardHeader className="px-0">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-slate-900 dark:text-foreground flex items-center gap-2">
                      <Clock3 className="w-5 h-5 text-blue-600 dark:text-link" />
                      Recent
                    </CardTitle>
                    <p className="text-sm text-slate-500 dark:text-muted-foreground mt-1">
                      Your most recently accessed audits
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowCreateReportDialog(true)}
                    className="bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border hover:bg-blue-700 text-white shadow-sm hover:shadow transition-all"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Report
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-0">
                <FilterBar
                  search={recentReportSearch}
                  onSearchChange={setRecentReportSearch}
                  templateFilter={recentReportTemplateFilter}
                  onTemplateFilterChange={setRecentReportTemplateFilter}
                  sort={recentReportSort}
                  onSortChange={setRecentReportSort}
                  templates={PREDEFINED_TEMPLATES}
                  showStatusFilter={false}
                  onClearAll={() => {
                    setRecentReportSearch("");
                    setRecentReportTemplateFilter("all");
                    setRecentReportSort("updated-desc");
                  }}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                />

                {loadingReports ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-link" />
                    <span className="ml-3 text-slate-500 dark:text-muted-foreground">
                      Loading reports...
                    </span>
                  </div>
                ) : filteredRecentReports.length > 0 ? (
                  <>
                    <div className="mt-4 text-sm text-slate-500 dark:text-muted-foreground">
                      Showing {filteredRecentReports.length} report
                      {filteredRecentReports.length > 1 ? "s" : ""}
                    </div>

                    {/* Conditional rendering based on view mode */}
                    {viewMode === "grid" ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-3">
                        {filteredRecentReports.map((report) =>
                          renderReportCard(report),
                        )}
                      </div>
                    ) : (
                      <ReportTableView
                        reports={filteredRecentReports}
                        getReportProgress={getReportProgress}
                        onOpenReport={openReport}
                        onGenerateReport={handleGenerateReport}
                        onDownloadReport={handleDownloadReport}
                        onEditReport={handleEditReport}
                        onDeleteReport={handleDeleteReport}
                        reportActionLoading={reportActionLoading}
                        formatReportDate={formatReportDate}
                        templateLabel={templateLabel}
                      />
                    )}
                  </>
                ) : (
                  // Empty state remains the same
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FileText className="w-12 h-12 text-slate-300 dark:text-muted-foreground mb-3" />
                    <h3 className="text-lg font-medium text-slate-900 dark:text-foreground mb-1">
                      No recent reports found
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-muted-foreground">
                      {recentReportSearch ||
                      recentReportTemplateFilter !== "all"
                        ? "Try adjusting your filters"
                        : "Create your first audit report to get started"}
                    </p>
                    {(recentReportSearch ||
                      recentReportTemplateFilter !== "all") && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => {
                          setRecentReportSearch("");
                          setRecentReportTemplateFilter("all");
                        }}
                      >
                        Clear filters
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </section>
          </div>
        </main>

        <Footer />
      </div>

      <NewAuditModal
        open={showNewAuditModal}
        onOpenChange={setShowNewAuditModal}
        onSiteCreated={() => {
          setShowNewAuditModal(false);
        }}
      />

      {currentSite && (
        <ReportModal
          open={showReportModal}
          onOpenChange={setShowReportModal}
          siteId={currentSite.id}
          siteName={currentSite.siteName}
          completionPercentage={Math.round(overallProgress)}
        />
      )}

      <AlertDialog
        open={Boolean(deletingReport)}
        onOpenChange={(open) => {
          if (!open && reportActionLoading?.action !== "delete")
            setDeletingReport(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold">
              Delete audit report?
            </AlertDialogTitle>
            <AlertDialogDescription className="mt-2 text-sm text-slate-600 dark:text-muted-foreground">
              Delete "{deletingReport?.reportName || "Untitled report"}" from
              the dashboard? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!isOnline && (
            <p className="mt-2 text-sm text-amber-700">
              Connect to the internet to delete this report.
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button
                variant="outline"
                disabled={reportActionLoading?.action === "delete"}
              >
                Cancel
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                variant="destructive"
                disabled={!isOnline || reportActionLoading?.action === "delete"}
                onClick={(event) => {
                  event.preventDefault();
                  handleConfirmDeleteReport();
                }}
              >
                {reportActionLoading?.action === "delete"
                  ? "Deleting..."
                  : "Delete report"}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={Boolean(editingReport)}
        onOpenChange={(open) => !open && setEditingReport(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                Report Name
              </label>
              <Input
                value={editReportName}
                onChange={(event) => setEditReportName(event.target.value)}
                placeholder="Enter report name"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setEditingReport(null)}
                disabled={updatingReportName}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveReportName}
                disabled={updatingReportName || !editReportName.trim()}
                className="bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border hover:bg-blue-700 text-white"
              >
                {updatingReportName ? "Saving..." : "Save Name"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCreateReportDialog}
        onOpenChange={handleCreateReportDialogChange}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Audit Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                Site
              </label>
              {false && currentSite ? (
                <div className="flex flex-col rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-border dark:bg-surface-raised/50">
                  <span className="font-medium text-slate-900 dark:text-foreground">
                    {currentSite.siteName}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-muted-foreground">
                    {currentSite.location} • {currentSite.siteType}
                  </span>
                </div>
              ) : (
                <Select
                  value={createReportSiteId}
                  onValueChange={(value) => {
                    if (value === "no") return;
                    setCreateReportSiteId(value);
                    const matchingSite = sites.find(
                      (site) => String(site.id || site._id) === String(value),
                    );
                    if (matchingSite) {
                      setAuditSelectedSite(matchingSite);
                      dispatch(setCurrentSiteData(matchingSite));
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choose a site" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites?.length === 0 ? (
                      <SelectItem value="no" disabled>
                        No sites available
                      </SelectItem>
                    ) : (
                      sites.map((site) => (
                        <SelectItem
                          key={site.id || site._id}
                          value={String(site.id || site._id)}
                        >
                          {site.siteName || site.name || "Untitled site"}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                Report Template
              </label>
              <Select
                value={newReportTemplateId}
                onValueChange={setNewReportTemplateId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a report template" />
                </SelectTrigger>
                <SelectContent>
                  {PREDEFINED_TEMPLATES.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template?.icon} {template.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                Report Name
              </label>
              <Input
                value={newReportName}
                onChange={(event) => setNewReportName(event.target.value)}
                placeholder="Enter report name"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => handleCreateReportDialogChange(false)}
                disabled={creatingReport}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateReport}
                disabled={
                  creatingReport ||
                  !newReportTemplateId ||
                  !newReportName.trim() ||
                  !createReportSiteId
                }
                className="bg-blue-600 dark:bg-primary dark:hover:bg-primary-hover dark:ring-1 dark:ring-inset dark:ring-primary-border hover:bg-blue-700 text-white"
              >
                {creatingReport ? "Creating..." : "Create Audit Report"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
